import React from "react";
import { maskCpfCnpj, maskPhone } from "../utils/masks.js";

/**
 * Componente para gerenciar detalhes de pagamento de forma dinâmica
 * Exibe campos específicos baseados na forma de pagamento selecionada
 */
const DetalhesPagamentoForm = ({ formData, handleInputChange, disabled = false }) => {
    const formaPagamento = formData.formaPagamento;
    const isPagamentoParcelado = formData.isPagamentoParcelado || false;

    const inputStyle = {
        width: "100%",
        padding: "10px 12px",
        border: "1px solid #D1D5DB",
        borderRadius: "8px",
        fontSize: "14px",
        backgroundColor: disabled ? "#F3F4F6" : "white"
    };

    const labelStyle = {
        display: "block",
        fontSize: "14px",
        fontWeight: "500",
        marginBottom: "6px",
        color: "#374151"
    };

    const sectionStyle = {
        backgroundColor: "#F9FAFB",
        padding: "16px",
        borderRadius: "8px",
        marginTop: "16px",
        border: "1px solid #E5E7EB"
    };

    const sectionTitleStyle = {
        fontSize: "15px",
        fontWeight: "600",
        marginBottom: "16px",
        color: "#1F2937",
        borderBottom: "2px solid #E5E7EB",
        paddingBottom: "8px"
    };

    const checkboxContainerStyle = {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "12px 16px",
        backgroundColor: "#F0F9FF",
        borderRadius: "8px",
        border: "1px solid #BFDBFE",
        marginTop: "16px",
        marginBottom: "16px"
    };

    const checkboxStyle = {
        width: "18px",
        height: "18px",
        cursor: disabled ? "not-allowed" : "pointer"
    };

    const checkboxLabelStyle = {
        fontSize: "14px",
        fontWeight: "500",
        color: "#1E40AF",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none"
    };

    // Função auxiliar para aplicar máscara em campos
    const handleMaskedChange = (e, maskFunction) => {
        const { name, value } = e.target;
        const maskedValue = maskFunction(value);
        handleInputChange({
            target: { name, value: maskedValue }
        });
    };

    // Máscara para código de barras (apenas números, 44 ou 47 dígitos)
    const maskCodigoBarras = (value) => {
        return value.replace(/\D/g, '').substring(0, 48);
    };

    // Máscara para código de banco (3 dígitos)
    const maskCodigoBanco = (value) => {
        return value.replace(/\D/g, '').substring(0, 3);
    };

    // Máscara para agência (4-5 dígitos com dígito verificador opcional)
    const maskAgencia = (value) => {
        let v = value.replace(/\D/g, '').substring(0, 6);
        if (v.length > 4) {
            v = v.replace(/^(\d{4})(\d)/, '$1-$2');
        }
        return v;
    };

    // Máscara para conta (com dígito verificador)
    const maskConta = (value) => {
        let v = value.replace(/\D/g, '').substring(0, 15);
        if (v.length > 1) {
            v = v.replace(/^(\d+)(\d{1})$/, '$1-$2');
        }
        return v;
    };

    // Renderizar campos específicos para PIX
    const renderPix = () => (
        <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>📱 Detalhes do PIX</h4>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Tipo de Chave PIX <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <select
                        name="pixTipoChave"
                        value={formData.pixTipoChave || ""}
                        onChange={handleInputChange}
                        style={inputStyle}
                        disabled={disabled}
                    >
                        <option value="">Selecione</option>
                        <option value="CPF">CPF</option>
                        <option value="CNPJ">CNPJ</option>
                        <option value="EMAIL">E-mail</option>
                        <option value="TELEFONE">Telefone</option>
                        <option value="ALEATORIA">Chave Aleatória</option>
                    </select>
                </div>

                <div>
                    <label style={labelStyle}>
                        Chave PIX <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="pixChave"
                        value={formData.pixChave || ""}
                        onChange={(e) => {
                            const tipo = formData.pixTipoChave;
                            if (tipo === "CPF" || tipo === "CNPJ") {
                                handleMaskedChange(e, maskCpfCnpj);
                            } else if (tipo === "TELEFONE") {
                                handleMaskedChange(e, maskPhone);
                            } else {
                                handleInputChange(e);
                            }
                        }}
                        placeholder={
                            formData.pixTipoChave === "CPF" ? "000.000.000-00" :
                            formData.pixTipoChave === "CNPJ" ? "00.000.000/0000-00" :
                            formData.pixTipoChave === "TELEFONE" ? "(00) 00000-0000" :
                            formData.pixTipoChave === "EMAIL" ? "email@exemplo.com" :
                            "Digite a chave PIX"
                        }
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div style={{ marginTop: "16px" }}>
                <label style={labelStyle}>Data de Vencimento</label>
                <input
                    type="date"
                    name="pixVencimento"
                    value={formData.pixVencimento || ""}
                    onChange={handleInputChange}
                    style={{ ...inputStyle, maxWidth: "200px" }}
                    disabled={disabled}
                />
            </div>
        </div>
    );

    // Renderizar campos específicos para Parcelamento (disponível para qualquer forma de pagamento)
    const renderParcelamento = () => (
        <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>💳 Detalhes do Parcelamento</h4>
            
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Número de Parcelas <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="number"
                        name="parcelamentoParcelas"
                        value={formData.parcelamentoParcelas || ""}
                        onChange={handleInputChange}
                        min="2"
                        max="120"
                        placeholder="Ex: 12"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        Intervalo <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <select
                        name="parcelamentoIntervalo"
                        value={formData.parcelamentoIntervalo || ""}
                        onChange={handleInputChange}
                        style={inputStyle}
                        disabled={disabled}
                    >
                        <option value="">Selecione</option>
                        <option value="SEMANAL">Semanal</option>
                        <option value="QUINZENAL">Quinzenal</option>
                        <option value="MENSAL">Mensal</option>
                        <option value="BIMESTRAL">Bimestral</option>
                        <option value="TRIMESTRAL">Trimestral</option>
                        <option value="SEMESTRAL">Semestral</option>
                    </select>
                </div>

                <div>
                    <label style={labelStyle}>
                        Data 1ª Parcela <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="date"
                        name="parcelamentoDataPrimeira"
                        value={formData.parcelamentoDataPrimeira ? formData.parcelamentoDataPrimeira.split('T')[0] : ""}
                        onChange={handleInputChange}
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div>
                <label style={labelStyle}>
                    Observações do Parcelamento
                </label>
                <textarea
                    name="parcelamentoObservacao"
                    value={formData.parcelamentoObservacao || ""}
                    onChange={handleInputChange}
                    placeholder="Ex: Juros aplicados, condições especiais, etc."
                    style={{ ...inputStyle, minHeight: "60px", resize: "vertical" }}
                    disabled={disabled}
                />
            </div>
        </div>
    );

    // Renderizar campos específicos para Transferência ou Boleto
    const renderDadosBancarios = () => (
        <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>🏦 Dados Bancários</h4>
            
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Nome do Banco <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="bancoNome"
                        value={formData.bancoNome || ""}
                        onChange={handleInputChange}
                        placeholder="Ex: Banco do Brasil"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        Código do Banco <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="bancoCodigo"
                        value={formData.bancoCodigo || ""}
                        onChange={(e) => handleMaskedChange(e, maskCodigoBanco)}
                        placeholder="Ex: 001"
                        maxLength="3"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Agência <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="bancoAgencia"
                        value={formData.bancoAgencia || ""}
                        onChange={(e) => handleMaskedChange(e, maskAgencia)}
                        placeholder="Ex: 1234-5"
                        maxLength="6"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        Conta <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="bancoConta"
                        value={formData.bancoConta || ""}
                        onChange={(e) => handleMaskedChange(e, maskConta)}
                        placeholder="Ex: 12345-6"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        Tipo de Conta <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <select
                        name="bancoTipoConta"
                        value={formData.bancoTipoConta || ""}
                        onChange={handleInputChange}
                        style={inputStyle}
                        disabled={disabled}
                    >
                        <option value="">Selecione</option>
                        <option value="CORRENTE">Corrente</option>
                        <option value="POUPANCA">Poupança</option>
                    </select>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Favorecido <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="bancoFavorecido"
                        value={formData.bancoFavorecido || ""}
                        onChange={handleInputChange}
                        placeholder="Nome do favorecido"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        CPF/CNPJ Favorecido <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="bancoCnpjCpfFavorecido"
                        value={formData.bancoCnpjCpfFavorecido || ""}
                        onChange={(e) => handleMaskedChange(e, maskCpfCnpj)}
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div style={{ marginTop: "16px" }}>
                <label style={labelStyle}>Data de Vencimento</label>
                <input
                    type="date"
                    name="transferenciaVencimento"
                    value={formData.transferenciaVencimento ? String(formData.transferenciaVencimento).split('T')[0] : ""}
                    onChange={handleInputChange}
                    style={{ ...inputStyle, maxWidth: "200px" }}
                    disabled={disabled}
                />
            </div>
        </div>
    );

    // Renderizar campos específicos para Boleto
    const renderBoleto = () => (
        <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>📄 Detalhes do Boleto</h4>
            
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Data de Vencimento <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                        type="date"
                        name="boletoVencimento"
                        value={formData.boletoVencimento ? formData.boletoVencimento.split('T')[0] : ""}
                        onChange={handleInputChange}
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        Número do Boleto
                    </label>
                    <input
                        type="text"
                        name="boletoNumero"
                        value={formData.boletoNumero || ""}
                        onChange={handleInputChange}
                        placeholder="Número do boleto"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>

                <div>
                    <label style={labelStyle}>
                        Código de Barras
                    </label>
                    <input
                        type="text"
                        name="boletoCodigoBarras"
                        value={formData.boletoCodigoBarras || ""}
                        onChange={(e) => handleMaskedChange(e, maskCodigoBarras)}
                        placeholder="Somente números (44-48 dígitos)"
                        maxLength="48"
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );

    // Renderizar campos específicos para Cartão
    const renderCartao = () => (
        <div style={sectionStyle}>
            <h4 style={sectionTitleStyle}>💳 Detalhes do Cartão</h4>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "16px" }}>
                <div>
                    <label style={labelStyle}>
                        Tipo de Cartão <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <select
                        name="cartaoTipo"
                        value={formData.cartaoTipo || ""}
                        onChange={handleInputChange}
                        style={inputStyle}
                        disabled={disabled}
                    >
                        <option value="">Selecione</option>
                        <option value="CREDITO">Crédito</option>
                        <option value="DEBITO">Débito</option>
                    </select>
                </div>

                <div>
                    <label style={labelStyle}>
                        Observações do Cartão
                    </label>
                    <input
                        type="text"
                        name="cartaoObservacao"
                        value={formData.cartaoObservacao || ""}
                        onChange={handleInputChange}
                        placeholder="Ex: Bandeira, últimos dígitos, etc."
                        style={inputStyle}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );

    // Renderizar observações adicionais
    const renderObservacoesAdicionais = () => (
        <div style={{ marginTop: "16px" }}>
            <label style={labelStyle}>
                Observações Adicionais sobre Pagamento
            </label>
            <textarea
                name="pagamentoObservacoes"
                value={formData.pagamentoObservacoes || ""}
                onChange={handleInputChange}
                placeholder="Informações adicionais sobre o pagamento"
                style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }}
                disabled={disabled}
            />
        </div>
    );

    // Renderização condicional baseada na forma de pagamento
    return (
        <div>
            {/* Renderizar campos específicos da forma de pagamento */}
            {formaPagamento === "PIX" && renderPix()}
            
            {/* Dados bancários apenas para Transferência (Boleto tem campos próprios) */}
            {formaPagamento === "TRANSFERENCIA" && renderDadosBancarios()}
            
            {/* Detalhes do Boleto: só exibir se NÃO for parcelado */}
            {formaPagamento === "BOLETO" && !isPagamentoParcelado && renderBoleto()}
            
            {formaPagamento === "CARTAO" && renderCartao()}
            
            {/* Checkbox para habilitar parcelamento (disponível para qualquer forma de pagamento) */}
            {formaPagamento && formaPagamento !== "" && (
                <div style={checkboxContainerStyle}>
                    <input
                        type="checkbox"
                        id="isPagamentoParcelado"
                        name="isPagamentoParcelado"
                        checked={isPagamentoParcelado}
                        onChange={(e) => {
                            const isChecked = e.target.checked;
                            
                            // Atualizar o checkbox
                            handleInputChange({
                                target: { name: "isPagamentoParcelado", value: isChecked }
                            });
                            
                            // Se desmarcado, limpar todos os campos de parcelamento
                            if (!isChecked) {
                                handleInputChange({
                                    target: { name: "parcelamentoParcelas", value: "" }
                                });
                                handleInputChange({
                                    target: { name: "parcelamentoIntervalo", value: "" }
                                });
                                handleInputChange({
                                    target: { name: "parcelamentoDataPrimeira", value: "" }
                                });
                                handleInputChange({
                                    target: { name: "parcelamentoObservacao", value: "" }
                                });
                            }
                        }}
                        style={checkboxStyle}
                        disabled={disabled}
                    />
                    <label htmlFor="isPagamentoParcelado" style={checkboxLabelStyle}>
                        💳 Este pagamento será parcelado
                    </label>
                </div>
            )}
            
            {/* Mostrar campos de parcelamento se o checkbox estiver marcado */}
            {isPagamentoParcelado && renderParcelamento()}
            
            {/* Observações adicionais para qualquer forma de pagamento */}
            {formaPagamento && formaPagamento !== "" && renderObservacoesAdicionais()}
        </div>
    );
};

export default DetalhesPagamentoForm;
